<template>
  <!-- Guide page opened from the WhatsApp group link (/c/{shortId}). Sign-in
       works like the rest of the site (AuthCard: Google or email); the site
       navbar and footer are hidden on this route. -->

  <div v-if="view === 'loading'" class="gt-loading full-screen">
    <b-loading :is-full-page="false" :active="true" />
  </div>

  <!-- 1. Sign in / create account -->
  <AuthCard
    v-else-if="view === 'login'"
    class="full-screen"
    :title="isJoin ? 'Guias Coreanos' : 'Entrar'"
    :subtitle="
      isJoin
        ? 'Cria a tua conta para veres e responderes aos tours Coreanos.'
        : 'Entra para veres o tour e responderes.'
    "
    :error="error"
  >
    <GoogleButton
      class="mt-5"
      :label="isJoin ? 'Criar conta com Google' : 'Continuar com Google'"
      :loading="busy === 'google'"
      @click="google"
    />
    <div class="divider" role="separator"><span>ou com o teu email</span></div>
    <form novalidate @submit.prevent="emailSubmit">
      <b-field label="Email">
        <b-input
          v-model.trim="email"
          type="email"
          autocomplete="email"
          placeholder="tu@exemplo.com"
          @input="error = ''"
        />
      </b-field>
      <b-field>
        <template #label>
          <span class="label-row">
            <span>Palavra-passe</span>
            <router-link
              v-if="!isJoin"
              class="small-link"
              :to="{ name: 'forgot-password', query: email ? { email } : {} }"
              >Esqueceste-te?</router-link
            >
          </span>
        </template>
        <b-input
          v-model="password"
          type="password"
          :autocomplete="isJoin ? 'new-password' : 'current-password'"
          password-reveal
          @input="error = ''"
        />
      </b-field>
      <p v-if="isJoin" class="hint">Pelo menos 8 caracteres.</p>
      <b-button
        type="is-primary"
        native-type="submit"
        expanded
        :loading="busy === 'email'"
        >{{ isJoin ? "Criar conta" : "Entrar" }}</b-button
      >
    </form>
    <template #footer>
      <template v-if="isJoin"
        >Já tens conta?
        <a href="#" @click.prevent="isJoin = false">Entrar</a></template
      >
      <template v-else
        >Ainda não tens conta?
        <a href="#" @click.prevent="isJoin = true">Criar conta</a></template
      >
    </template>
  </AuthCard>

  <!-- 2. Name and WhatsApp -->
  <AuthCard
    v-else-if="view === 'profile'"
    class="full-screen"
    title="Quase pronto"
    subtitle="Diz-nos o teu nome e o teu WhatsApp, para falarmos contigo sobre os tours."
    :error="error"
  >
    <form class="mt-5" novalidate @submit.prevent="register">
      <b-field label="Nome e apelido"
        ><b-input v-model.trim="name" autocomplete="name"
      /></b-field>
      <b-field
        label="WhatsApp"
        message="Com o indicativo, por exemplo +351 912 345 678"
      >
        <b-input
          v-model.trim="whatsapp"
          type="tel"
          autocomplete="tel"
          placeholder="+351 912 345 678"
        />
      </b-field>
      <b-button
        type="is-primary"
        native-type="submit"
        expanded
        :loading="busy === 'register'"
        >Enviar pedido de acesso</b-button
      >
    </form>
    <template #footer><a href="#" @click.prevent="signOut">Sair</a></template>
  </AuthCard>

  <!-- 3. Waiting for approval / inactive -->
  <AuthCard
    v-else-if="view === 'pending'"
    class="full-screen"
    :title="
      guide && guide.status === 'inactive'
        ? 'Conta inativa'
        : 'À espera de aprovação'
    "
    :subtitle="
      guide && guide.status === 'inactive'
        ? 'Fala com a equipa se achas que é um engano.'
        : `Obrigado, ${firstName}! O teu registo foi concluído com sucesso.`
    "
  >
    <template v-if="!guide || guide.status !== 'inactive'">
      <p class="pending-text">
        A tua conta está à espera de aprovação pela equipa. Assim que for
        aprovada, recebes um email de confirmação e podes voltar a entrar aqui
        para ver os novos tours e outras oportunidades disponíveis.
      </p>
      <button
        type="button"
        class="btn-lime pending-btn"
        :disabled="busy === 'approved'"
        @click="checkApproved"
      >
        {{ busy === "approved" ? "A verificar…" : "Já fui aprovado" }}
      </button>
    </template>
    <template #footer><a href="#" @click.prevent="signOut">Sair</a></template>
  </AuthCard>

  <p v-else-if="view === 'error'" class="gt-error full-screen">{{ error }}</p>

  <!-- 4. Personal page (/c): my tours, waiting for the team, looking for people -->
  <div v-else-if="view === 'home'" class="gt full-screen">
    <header class="gt-head">
      <div class="gt-top">
        <img :src="brand.logoFullWhite" alt="Guiders" class="brand" />
        <a href="#" class="hello" @click.prevent="signOut">Sair</a>
      </div>
      <p class="eyebrow">Tours Coreanos</p>
      <h1 class="gt-title">Olá, {{ firstName }}</h1>
    </header>
    <main class="gt-body">
      <!-- Private Telegram messages: approved for a tour, 24 h reminder -->
      <div v-if="!(guide.telegram && tgHidden)" class="card tg-card">
        <template v-if="guide.telegram">
          <button type="button" class="tg-close" aria-label="Fechar" @click="hideTelegramCard">
            <b-icon icon="times" size="is-small" />
          </button>
          <p class="tg-title">
            <b-icon icon="paper-plane" size="is-small" />Telegram ligado{{
              guide.telegram.username ? ` como @${guide.telegram.username}` : ""
            }}
          </p>
          <p class="tg-text">
            Recebes uma mensagem privada quando fores aprovado para um tour e um
            lembrete na véspera às 17h.
          </p>
        </template>
        <template v-else>
          <p class="tg-title">
            <b-icon icon="paper-plane" size="is-small" />Avisos no Telegram
          </p>
          <p class="tg-text">
            Liga o teu Telegram para receberes uma mensagem privada quando fores
            aprovado e um lembrete na véspera às 17h. No grupo passas a aparecer
            mencionado na lista.
          </p>
          <button
            type="button"
            class="btn-lime tg-btn"
            :disabled="busy === 'telegram'"
            @click="linkTelegram"
          >
            {{
              waitingTelegram ? "À espera do Telegram…" : "Ligar o meu Telegram"
            }}
          </button>
          <p v-if="waitingTelegram" class="tg-text small">
            No Telegram, carrega em "Começar". Esta página atualiza sozinha.
          </p>
        </template>
      </div>

      <!-- What they earn per month (tours on the team x pay per guide) -->
      <section v-if="earnings.length" class="home-group">
        <h2 class="home-title">Os teus ganhos <span>estimativa</span></h2>
        <div class="card earn-list">
          <div v-for="e in earnings" :key="e.month" class="earn-row">
            <span>{{ monthLabel(e.month) }}</span>
            <span class="small">{{ earnNote(e) }}</span>
            <strong>{{ euros(e.amount) }}</strong>
          </div>
        </div>
      </section>

      <section v-for="group in homeGroups" :key="group.key" class="home-group">
        <h2 class="home-title">
          {{ group.title }} <span>{{ group.tours.length }}</span>
        </h2>
        <p v-if="!group.tours.length" class="home-empty">{{ group.empty }}</p>
        <router-link
          v-for="t in group.tours"
          :key="t.id"
          :to="{ name: 'coreanos-tour', params: { shortId: t.shortId } }"
          class="card home-tour"
        >
          <div>
            <p class="home-when">{{ dayLabel(t.date) }} · {{ t.startTime }}</p>
            <p class="home-route">
              {{ t.pickup && t.pickup.label }} →
              {{ t.dropoff ? t.dropoff.label : "sem drop-off" }}
            </p>
            <p class="home-meta">
              {{ tuksLabel(t.tuksTotal) }}<template
                v-if="t.seats && t.mine !== 'unavailable'"
              >
                · o teu tuk: {{ t.seats }} lugares</template
              ><template v-if="t.guidePay != null">
                · {{ euros(t.guidePay) }}</template
              >
            </p>
          </div>
          <span class="home-tag" :class="`is-${group.key}`">{{
            group.tag(t)
          }}</span>
        </router-link>
      </section>
    </main>
  </div>

  <!-- 4. The tour (approved guides only) -->
  <div v-else class="gt full-screen">
    <header class="gt-head">
      <div class="gt-top">
        <router-link :to="{ name: 'coreanos-guide-home' }" class="brand"
          ><img :src="brand.logoFullWhite" alt="Guiders"
        /></router-link>
        <router-link :to="{ name: 'coreanos-guide-home' }" class="hello"
          >Os meus tours →</router-link
        >
      </div>
      <span v-if="mine" class="in-tour"
        ><b-icon icon="check" size="is-small" />Estás neste tour</span
      >
      <p v-else class="eyebrow">Tour Coreanos</p>
      <h1 class="gt-title">{{ dayLabel(tour.date) }} · {{ tour.startTime }}</h1>
      <p v-if="mine && response" class="gt-sub">
        Com o teu tuk de {{ response.seats }} lugares
      </p>
      <!-- <p v-else-if="statusLine" class="status-line">
        <b-icon icon="clock" size="is-small" />{{ statusLine }}
      </p> -->
    </header>

    <main class="gt-body">
      <div class="card details">
        <div class="detail">
          <div>
            <p class="label">Pick-up</p>
            <p class="value">{{ tour.pickup && tour.pickup.label }}</p>
          </div>
        </div>
        <div class="detail">
          <div>
            <p class="label">Drop-off</p>
            <p class="value">
              {{ tour.dropoff ? tour.dropoff.label : "Sem drop-off" }}
            </p>
          </div>
        </div>
        <div class="detail">
          <div>
            <p class="label">Pagamento</p>
            <p class="value">
              {{ tour.guidePay == null ? "A confirmar com a equipa" : `${euros(tour.guidePay)} por guia` }}
            </p>
          </div>
        </div>
        <div class="detail">
          <div>
            <p class="label">Tuk-tuks</p>
            <p class="value">
              {{ tour.tuksTotal
              }}{{
                tour.spots === "full"
                  ? " · lista completa"
                  : tour.spots
                    ? ` · ${tour.spots === 1 ? "falta" : "faltam"} ${tour.spots}`
                    : ""
              }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="tour.status === 'cancelled'" class="card notice">
        <p class="notice-title">Tour cancelado</p>
        <p>Obrigado a todos os que se ofereceram.</p>
      </div>

      <!-- The tour has started: nothing to answer any more -->
      <div v-else-if="started" class="card notice">
        <p class="notice-title">
          <b-icon icon="lock" size="is-small" />Este tour já começou
        </p>
        <p>Já não dá para responder aqui. Fala com a equipa se precisares.</p>
      </div>

      <!-- Date / time changed: confirm within 15 min (they keep the spot) -->
      <template v-else-if="needsReconfirm">
        <div class="card change">
          <p class="change-title">
            <b-icon icon="exclamation-triangle" size="is-small" />{{
              changeTitle
            }}
          </p>
          <div class="change-values">
            <div class="before">
              <span>Antes</span><s>{{ changeBefore }}</s>
            </div>
            <b-icon icon="arrow-right" size="is-small" />
            <div class="after">
              <span>Agora</span><strong>{{ changeAfter }}</strong>
            </div>
          </div>
          <p>
            Confirma se continuas disponível. Mantens o lugar se não
            responderes, mas a equipa vai ligar-te.
          </p>
          <p v-if="reconfirmMinutes > 0" class="change-deadline">
            Responde nos próximos {{ reconfirmMinutes }} min
          </p>
        </div>
        <div class="bottom">
          <button
            type="button"
            class="btn-lime"
            :disabled="!!busy"
            @click="reconfirm(true)"
          >
            Continuo disponível às {{ tour.startTime }}
          </button>
          <button
            type="button"
            class="btn-outline danger"
            :disabled="!!busy"
            @click="reconfirm(false)"
          >
            Já não posso
          </button>
          <p class="fine">
            Podes cancelar até 30 min antes do início ({{ lockLabel }}).
          </p>
        </div>
      </template>

      <!-- On the team: swap with someone (any time) or cancel (until 30 min before) -->
      <template v-else-if="mine">
        <div class="card answer-state">
          <span class="state is-assigned">Estás neste tour</span>
          <p>
            Contamos contigo. Se deixares de poder, troca com outro guia
            disponível{{ locked ? "" : " ou cancela aqui" }} o mais cedo
            possível.
          </p>
        </div>
        <div v-if="locked" class="card notice">
          <p class="notice-title">
            <b-icon icon="lock" size="is-small" />Já não é possível cancelar
          </p>
          <p>
            Faltam menos de 30 min para o início. Podes trocar com outro guia
            disponível ou falar diretamente com a equipa.
          </p>
        </div>
        <div class="bottom">
          <button
            type="button"
            class="btn-lime"
            :disabled="!!busy"
            @click="openSwap"
          >
            Trocar com outro guia
          </button>
          <button
            v-if="!locked"
            type="button"
            class="btn-outline danger"
            :disabled="!!busy"
            @click="answer('unavailable')"
          >
            Já não posso ir
          </button>
          <template v-else>
            <a :href="`tel:${adminPhone.replace(/\s/g, '')}`" class="btn-dark"
              ><b-icon icon="phone-alt" size="is-small" />Ligar ao admin</a
            >
            <a
              :href="adminWhatsapp"
              target="_blank"
              rel="noopener"
              class="btn-outline"
              ><b-icon icon="comment" size="is-small" />WhatsApp ao admin</a
            >
          </template>
          <p class="fine">
            <template v-if="!locked"
              >Podes cancelar até 30 min antes do início ({{ lockLabel }}).
            </template>
            Trocar com outro guia podes a qualquer hora até ao início.
          </p>
        </div>
      </template>

      <!-- Already answered (not on the team) -->
      <template v-else-if="response && !changing">
        <div class="card answer-state">
          <span class="state" :class="`is-${state.key}`">{{
            state.label
          }}</span>
          <p v-if="state.key === 'waiting'">{{ waitingText }}</p>
          <p v-else-if="state.key === 'waitlist'">
            Já temos todos os guias para este tour e estás na lista de espera.
            Se alguém desistir, os guias são chamados pela ordem em que
            indicaram a sua disponibilidade.
          </p>
          <p v-else-if="state.key === 'not'">
            Desta vez a equipa escolheu outros guias. Obrigado por te
            ofereceres!
          </p>
          <p v-else>Obrigado por avisares.</p>
        </div>
        <div class="bottom">
          <button
            v-if="response.availability === 'available'"
            type="button"
            class="btn-outline danger"
            :disabled="!!busy"
            @click="answer('unavailable')"
          >
            Já não estou disponível
          </button>
          <button
            v-else
            type="button"
            class="btn-lime"
            :disabled="!!busy"
            @click="changing = true"
          >
            Afinal estou disponível
          </button>
        </div>
      </template>

      <!-- Answer -->
      <template v-else>
        <div v-if="tour.status === 'staffed'" class="card answer-state">
          <span class="state is-waitlist">Lista completa</span>
          <p>
            Este tour já tem todos os guias, mas podes ficar na lista de espera.
            Se alguém desistir, os guias são chamados pela ordem em que
            indicaram a sua disponibilidade.
          </p>
        </div>
        <div class="card seats">
          <p class="seats-q">Quantos lugares tem o teu tuk neste tour?</p>
          <div class="seat-options">
            <button
              type="button"
              :class="{ 'is-on': seats === 4 }"
              @click="seats = 4"
            >
              4 lugares
            </button>
            <button
              type="button"
              :class="{ 'is-on': seats === 6 }"
              @click="seats = 6"
            >
              6 lugares
            </button>
          </div>
        </div>
        <div class="bottom">
          <button
            type="button"
            class="btn-lime"
            :disabled="!!busy"
            @click="answer('available')"
          >
            Estou disponível
          </button>
          <button
            v-if="!response || response.availability !== 'unavailable'"
            type="button"
            class="btn-outline"
            :disabled="!!busy"
            @click="answer('unavailable')"
          >
            Não posso
          </button>
          <button
            v-else
            type="button"
            class="btn-outline"
            :disabled="!!busy"
            @click="changing = false"
          >
            Voltar
          </button>
        </div>
      </template>
    </main>

    <!-- Swap: everyone available who isn't on the team; only eligible ones can be chosen -->
    <div
      v-if="swapOpen"
      ref="swapOverlay"
      class="swap-overlay"
      tabindex="-1"
      @click.self="swapOpen = false"
      @keydown.esc="swapOpen = false"
    >
      <div class="card swap-card" role="dialog" aria-modal="true">
        <p class="swap-title">Trocar com outro guia</p>
        <p class="swap-text">
          Escolhe quem fica com o teu lugar. Aparecem os guias que disseram que
          estão disponíveis para este tour.
        </p>
        <p v-if="!swapOptions.length" class="swap-empty">
          Ainda ninguém está disponível para trocar. Fala com a equipa.
        </p>
        <button
          v-for="o in swapOptions"
          :key="o.id"
          type="button"
          class="swap-option"
          :class="{ 'is-on': swapPick === o.id, 'is-off': !o.eligible }"
          :disabled="!o.eligible"
          @click="swapPick = o.id"
        >
          <span class="swap-name">{{ o.name }}</span>
          <span class="swap-seats">{{ o.seats }} lugares</span>
          <span v-if="!o.eligible" class="swap-reason">{{ o.reason }}</span>
        </button>
        <div class="swap-actions">
          <button type="button" class="btn-outline" @click="swapOpen = false">
            Voltar
          </button>
          <button
            type="button"
            class="btn-lime"
            :disabled="!swapPick || !!busy"
            @click="confirmSwap"
          >
            Confirmar troca
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from "firebase/app";
import "firebase/auth";
import AuthCard from "@/components/auth/AuthCard.vue";
import GoogleButton from "@/components/auth/GoogleButton.vue";
import { authErrorMessage } from "@/utils/authErrors";
import loadThemeFont from "@/components/home/loadThemeFont";
import { brand } from "@/data/homepage";
import {
  guideRegister,
  getGuideTour,
  getGuideHome,
  linkGuideTelegram,
  getGuideMe,
  respondToTour,
  reconfirmTour,
  swapTourGuide,
  dayLabel,
  minutesUntil,
  tuksLabel,
  plural,
} from "@/services/coreanos";

const LOCK_MS = 30 * 60000;
const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || "");

export default {
  name: "GuideTour",
  components: { AuthCard, GoogleButton },
  metaInfo: {
    title: "Tour Coreanos | Guiders",
    meta: [{ name: "robots", content: "noindex, nofollow" }],
  },
  data() {
    return {
      brand,
      view: "loading", // loading | login | profile | pending | tour | home | error
      homeTours: [],
      earnings: [],
      waitingTelegram: false,
      tgHidden: false,
      isJoin: true,
      error: "",
      busy: "",
      email: "",
      password: "",
      name: "",
      whatsapp: "+351 ",
      guide: null,
      tour: null,
      response: null,
      seats: 4,
      changing: false,
      swapOptions: [],
      swapOpen: false,
      swapPick: "",
      now: Date.now(),
      adminPhone: brand.phoneAdmin,
      adminWhatsapp: brand.whatsappAdmin,
    };
  },
  computed: {
    homeGroups() {
      const t = this.homeTours;
      return [
        {
          key: "assigned",
          title: "Os teus tours",
          empty: "Ainda não estás em nenhum tour.",
          tours: t.filter((x) => x.mine === "assigned"),
          tag: () => "Estás neste tour",
        },
        {
          key: "waiting",
          title: "À espera de seleção",
          empty: "Nenhum.",
          tours: t.filter((x) => ["waiting", "waitlist"].includes(x.mine)),
          tag: (x) =>
            x.mine === "waitlist" ? "Na lista de espera" : "Disseste que podes",
        },
        {
          key: "open",
          title: "Tours abertos",
          empty: "Não há tours abertos agora.",
          tours: t.filter((x) => !["assigned", "waiting", "waitlist"].includes(x.mine)),
          tag: (x) =>
            x.mine === "unavailable"
              ? "Disseste que não"
              : x.needsPeople
                ? "Diz se podes"
                : "Completo · lista de espera",
        },
      ];
    },
    shortId() {
      return this.$route.params.shortId;
    },
    firstName() {
      return ((this.guide && this.guide.name) || "").split(" ")[0];
    },
    mine() {
      return (
        this.response &&
        this.response.availability === "available" &&
        this.response.assignment === "assigned"
      );
    },
    windowOpen() {
      return (
        this.tour &&
        this.tour.selectionClosesAt &&
        new Date(this.tour.selectionClosesAt) > this.now &&
        !this.tour.selectionDone
      );
    },
    statusLine() {
      if (!this.tour || this.tour.status === "cancelled") return "";
      return this.windowOpen
        ? `Respostas abertas · seleção dentro de ${minutesUntil(
            this.tour.selectionClosesAt,
          )} min`
        : "Respostas abertas";
    },
    started() {
      return new Date(this.tour.startAt).getTime() <= this.now;
    },
    // Only matters for guides on the team (cancel until 30 min before)
    locked() {
      return new Date(this.tour.startAt).getTime() - LOCK_MS < this.now;
    },
    lockLabel() {
      const at = new Date(new Date(this.tour.startAt).getTime() - LOCK_MS);
      return new Intl.DateTimeFormat("pt-PT", {
        timeZone: "Europe/Lisbon",
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }).format(at);
    },
    needsReconfirm() {
      const r = this.response;
      return !!(
        r &&
        r.assignment === "assigned" &&
        r.reconfirm &&
        ["pending", "not_reconfirmed"].includes(r.reconfirm.status)
      );
    },
    dateChanged() {
      const rc = this.response.reconfirm;
      return rc.before && rc.after && rc.before.date !== rc.after.date;
    },
    changeTitle() {
      return this.dateChanged ? "A data do tour mudou" : "A hora do tour mudou";
    },
    changeBefore() {
      const b = this.response.reconfirm.before || {};
      return this.dateChanged
        ? `${dayLabel(b.date)} · ${b.startTime}`
        : b.startTime;
    },
    changeAfter() {
      const a = this.response.reconfirm.after || {};
      return this.dateChanged
        ? `${dayLabel(a.date)} · ${a.startTime}`
        : a.startTime;
    },
    reconfirmMinutes() {
      const rc = this.response.reconfirm;
      return rc.status === "pending" && rc.deadline
        ? minutesUntil(rc.deadline)
        : 0;
    },
    state() {
      const r = this.response;
      if (r.availability !== "available")
        return { key: "no", label: "Não disponível" };
      if (r.assignment === "assigned")
        return { key: "assigned", label: "Estás neste tour" };
      // The team chooses who goes; the suggestion is never shown to guides
      if (r.assignment === "removed")
        return { key: "not", label: "Não selecionado" };
      if (this.tour.status === "staffed")
        return { key: "waitlist", label: "Na lista de espera" };
      return { key: "waiting", label: "Aguarda seleção" };
    },
    waitingText() {
      if (this.windowOpen) {
        return `Resposta registada. As respostas fecham dentro de ${minutesUntil(
          this.tour.selectionClosesAt,
        )} min; depois a equipa escolhe quem vai.`;
      }
      return "Resposta registada. A equipa escolhe quem vai e confirma-te aqui e no grupo.";
    },
  },
  watch: {
    "guide.id"() {
      try {
        this.tgHidden = localStorage.getItem(this.tgHiddenKey()) === "1";
      } catch (e) {
        this.tgHidden = false;
      }
    },
    // From the personal page to a tour (or back): load the new view
    $route() {
      this.changing = false;
      this.view = "loading";
      this.load();
    },
  },
  created() {
    loadThemeFont();
  },
  mounted() {
    this.timer = setInterval(() => (this.now = Date.now()), 30000);
    this.stopAuth = firebase.auth().onAuthStateChanged((user) => {
      if (this.view !== "loading") return;
      if (user) this.load();
      else this.view = "login";
    });
  },
  beforeDestroy() {
    clearInterval(this.timer);
    clearInterval(this.tgTimer);
    if (this.stopAuth) this.stopAuth();
  },
  methods: {
    tuksLabel,
    plural,
    dayLabel,
    euros(v) {
      return `${Number(v || 0).toLocaleString("pt-PT", { maximumFractionDigits: 2 })} €`;
    },
    // "3 tours · 2 feitos · 1 sem valor ainda" (same numbers as /ganhos on Telegram)
    earnNote(e) {
      const done = (e.done && e.done.tours) || 0;
      return [plural(e.tours, "tour", "tours"), done < e.tours ? `${done} ${done === 1 ? "feito" : "feitos"}` : "", e.unpriced ? `${e.unpriced} sem valor ainda` : ""]
        .filter(Boolean)
        .join(" · ");
    },
    monthLabel(key) {
      const names = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
      const [y, m] = key.split("-").map(Number);
      return `${names[m - 1]} ${y}`;
    },

    // Who is this, and can they see the tour?
    async load() {
      if (!this.shortId) return this.loadHome();
      try {
        const data = await getGuideTour(this.shortId);
        this.guide = data.guide;
        this.tour = data.tour;
        this.response = data.response;
        this.swapOptions = data.swapOptions || [];
        if (this.response && this.response.seats)
          this.seats = this.response.seats;
        if (!this.guide) {
          const user = firebase.auth().currentUser;
          this.name = this.name || (user && user.displayName) || "";
          this.view = "profile";
        } else if (!this.tour) this.view = "pending";
        else this.view = "tour";
      } catch (error) {
        this.error = error.message;
        this.view = "error";
      }
    },

    // Opens the bot with a one-time code; the backend links the account when
    // the guide taps "Começar". Checks every few seconds for 3 minutes.
    // "Telegram ligado" card closed with the ×: remembered in this browser
    tgHiddenKey() {
      return `coreanos-tg-hidden-${this.guide && this.guide.id}`;
    },
    hideTelegramCard() {
      this.tgHidden = true;
      try {
        localStorage.setItem(this.tgHiddenKey(), "1");
      } catch (e) {
        // private mode: hidden until the page reloads
      }
    },
    async linkTelegram() {
      this.busy = "telegram";
      const tab = window.open("", "_blank");
      try {
        const { url } = await linkGuideTelegram();
        if (tab) tab.location = url;
        else window.location.href = url;
        this.waitingTelegram = true;
        clearInterval(this.tgTimer);
        const started = Date.now();
        this.tgTimer = setInterval(async () => {
          const me = await getGuideMe().catch(() => null);
          if (me && me.telegram) {
            this.guide = me;
            this.waitingTelegram = false;
            clearInterval(this.tgTimer);
          } else if (Date.now() - started > 3 * 60000) {
            this.waitingTelegram = false;
            clearInterval(this.tgTimer);
          }
        }, 4000);
      } catch (error) {
        if (tab) tab.close();
        this.$buefy.toast.open({
          message: error.message,
          type: "is-danger",
          position: "is-bottom",
        });
      } finally {
        this.busy = "";
      }
    },

    async loadHome() {
      try {
        const data = await getGuideHome();
        this.guide = data.guide;
        if (!this.guide) {
          const user = firebase.auth().currentUser;
          this.name = this.name || (user && user.displayName) || "";
          this.view = "profile";
        } else if (!data.tours) this.view = "pending";
        else {
          this.homeTours = data.tours;
          this.earnings = data.earnings || [];
          this.view = "home";
        }
      } catch (error) {
        this.error = error.message;
        this.view = "error";
      }
    },

    // "Já fui aprovado": checks again; the page opens if the account is active
    async checkApproved() {
      this.busy = "approved";
      await this.load();
      this.busy = "";
      if (this.view === "pending") {
        this.$buefy.toast.open({
          message: "A tua conta ainda está à espera de aprovação.",
          type: "is-warning",
          position: "is-bottom",
        });
      }
    },

    openSwap() {
      this.swapPick = "";
      this.swapOpen = true;
      this.$nextTick(() => this.$refs.swapOverlay && this.$refs.swapOverlay.focus());
    },
    async confirmSwap() {
      const pick = this.swapOptions.find((o) => o.id === this.swapPick);
      if (!pick) return;
      this.busy = "swap";
      try {
        await swapTourGuide(this.shortId, pick.id);
        this.swapOpen = false;
        this.$buefy.toast.open({
          message: `Troca feita. ${pick.name} fica com o teu lugar.`,
          type: "is-success",
          position: "is-bottom",
        });
        await this.load();
      } catch (error) {
        this.$buefy.toast.open({
          message: error.message,
          type: "is-danger",
          position: "is-bottom",
        });
      } finally {
        this.busy = "";
      }
    },

    async reload() {
      this.busy = "reload";
      await this.load();
      this.busy = "";
    },

    async emailSubmit() {
      this.error = "";
      if (!isEmail(this.email))
        return (this.error = "Escreve um email válido.");
      if (this.isJoin && this.password.length < 8)
        return (this.error =
          "A palavra-passe precisa de pelo menos 8 caracteres.");
      if (!this.password) return (this.error = "Escreve a tua palavra-passe.");
      this.busy = "email";
      try {
        const auth = firebase.auth();
        if (this.isJoin) {
          try {
            await auth.createUserWithEmailAndPassword(
              this.email,
              this.password,
            );
          } catch (error) {
            // Already has a Guiders login: use it
            if (error.code !== "auth/email-already-in-use") throw error;
            await auth.signInWithEmailAndPassword(this.email, this.password);
          }
        } else {
          await auth.signInWithEmailAndPassword(this.email, this.password);
        }
        await this.load();
      } catch (error) {
        this.error = authErrorMessage(error);
      } finally {
        this.busy = "";
      }
    },

    async google() {
      this.error = "";
      this.busy = "google";
      try {
        const provider = new firebase.auth.GoogleAuthProvider();
        provider.setCustomParameters({ prompt: "select_account" });
        await firebase.auth().signInWithPopup(provider);
        await this.load();
      } catch (error) {
        this.error = authErrorMessage(error);
      } finally {
        this.busy = "";
      }
    },

    async register() {
      this.error = "";
      if (this.name.length < 2)
        return (this.error = "Escreve o teu nome e apelido.");
      if (this.whatsapp.replace(/\D/g, "").length < 9)
        return (this.error =
          "Escreve o teu número de WhatsApp com o indicativo.");
      this.busy = "register";
      try {
        this.guide = await guideRegister(this.name, this.whatsapp);
        await this.load();
      } catch (error) {
        this.error = error.message;
      } finally {
        this.busy = "";
      }
    },

    async signOut() {
      await firebase.auth().signOut();
      Object.assign(this, {
        guide: null,
        tour: null,
        response: null,
        error: "",
        view: "login",
      });
    },

    async reconfirm(keep) {
      this.busy = "reconfirm";
      try {
        await reconfirmTour(this.shortId, keep);
        await this.load();
      } catch (error) {
        this.$buefy.toast.open({
          message: error.message,
          type: "is-danger",
          position: "is-bottom",
        });
      } finally {
        this.busy = "";
      }
    },

    async answer(availability) {
      this.busy = "answer";
      try {
        await respondToTour(this.shortId, availability, this.seats);
        this.changing = false;
        await this.load();
      } catch (error) {
        this.$buefy.toast.open({
          message: error.message,
          type: "is-danger",
          position: "is-bottom",
        });
      } finally {
        this.busy = "";
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "./coreanos";

.gt-loading {
  position: relative;
  min-height: 100vh;
}

.gt-error {
  padding: 3rem 1.5rem;
  text-align: center;
  color: $ink-sub;
}

// Auth cards (same layout as the other login pages)
.divider {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 1.25rem 0;
  font-size: 0.8125rem;
  color: $ink-sub;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: $line;
  }
}

.label-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.small-link {
  font-size: 0.8125rem;
  font-weight: 500;
  color: $forest-2;
  text-decoration: underline;
}

.hint {
  margin: -0.5rem 0 1rem;
  font-size: 0.8125rem;
  color: $ink-sub;
}

// ---------- Tour ----------
.gt {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  background: $page;
  color: $ink;
}

.gt-head {
  padding: 1.5rem 1.5rem 1.75rem;
  background: $forest;
  color: #fff;
}

.gt-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.brand {
  display: block;
  height: 1.5rem;
  width: auto;

  img {
    display: block;
    height: 100%;
    width: auto;
  }
}

.hello {
  font-family: $display;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
}

.eyebrow {
  @include eyebrow;
  color: $lime;
}

.gt-title {
  margin-top: 0.375rem;
  font-family: $display;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: #fff;
}

.gt-sub {
  margin-top: 0.5rem;
  font-size: 1.0625rem;
  color: rgba(255, 255, 255, 0.85);
}

.in-tour {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.875rem;
  border-radius: 999px;
  background: $lime;
  font-family: $display;
  font-weight: 700;
  color: $forest;
}

.status-line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.625rem 1rem;
  border-radius: 12px;
  background: $forest-2;
  font-family: $display;
  font-weight: 600;
}

.gt-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 34rem;
  margin: 0 auto;
  padding: 1.5rem 1.25rem;
}

.card {
  @include card;
  padding: 1.25rem;
}

.details {
  display: grid;
  gap: 1rem;
}

.detail {
  display: flex;
  gap: 0.875rem;

  > .icon {
    color: $forest-2;
  }
}

.label {
  @include eyebrow;
  color: #2c6e57;
}

.value {
  font-family: $display;
  font-size: 1.125rem;
  font-weight: 600;
  color: $forest;
}

.notice-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.375rem;
  font-family: $display;
  font-size: 1.125rem;
  font-weight: 700;
  color: $forest;
}

.notice p:not(.notice-title) {
  color: $ink-sub;
}

.tg-close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: none;
  color: inherit;
  opacity: 0.6;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
}

.tg-card {
  position: relative;
  border-color: $lime;
}

.tg-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: $display;
  font-size: 1.0625rem;
  font-weight: 600;
  color: $forest;
}

.tg-text {
  margin-top: 0.375rem;
  font-size: 0.9375rem;
  color: $ink-sub;

  &.small {
    font-size: 0.8125rem;
  }
}

.tg-btn {
  width: 100%;
  min-height: 3rem;
  margin-top: 0.875rem;
  font-size: 1rem;
}

.home-group {
  display: grid;
  gap: 0.625rem;

  & + & {
    margin-top: 1rem;
  }
}

.home-title {
  font-family: $display;
  font-size: 1.125rem;
  font-weight: 600;
  color: $forest;

  span {
    font-weight: 400;
    color: $ink-faint;
  }
}

.home-empty {
  font-size: 0.9375rem;
  color: $ink-sub;
}

.home-tour {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  color: inherit;
}

.home-when {
  font-family: $display;
  font-size: 1.0625rem;
  font-weight: 600;
  color: $forest;
}

.home-route {
  margin-top: 0.125rem;
  color: $ink;
}

.home-meta {
  margin-top: 0.125rem;
  font-size: 0.875rem;
  color: $ink-sub;
}

.home-tag {
  flex-shrink: 0;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  background: $soft;
  font-size: 0.8125rem;
  font-weight: 500;
  color: $forest;

  &.is-assigned {
    background: $lime;
  }
  &.is-open {
    background: $warn-bg;
    color: $warn-ink;
  }
}

.change {
  border: 2px solid $orange;
  background: $warn-soft;
  color: $warn-ink;

  p {
    margin-top: 0.75rem;
  }
}

.change-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0 !important;
  font-family: $display;
  font-size: 1.25rem;
  font-weight: 700;
}

.change-values {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0.75rem;
  align-items: center;
  margin-top: 0.875rem;

  > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.625rem;
    border-radius: 12px;
    font-family: $display;
  }

  span {
    font-size: 0.875rem;
    font-weight: 600;
  }

  .before {
    background: #fff;

    s {
      font-size: 1.375rem;
      font-weight: 700;
    }
  }

  .after {
    background: $forest;
    color: #fff;

    span {
      color: $lime;
    }

    strong {
      font-size: 1.375rem;
      color: #fff;
    }
  }
}

.change-deadline {
  font-weight: 700;
}

.seats-q {
  margin-bottom: 0.875rem;
  font-family: $display;
  font-size: 1.125rem;
  font-weight: 700;
  color: $forest;
}

.seat-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  button {
    min-height: 4rem;
    border: 1.5px solid $line;
    border-radius: 14px;
    background: #fff;
    font-family: $display;
    font-size: 1.25rem;
    font-weight: 700;
    color: $forest;
    cursor: pointer;

    &.is-on {
      border-color: $forest;
      background: $forest;
      color: $lime;
    }
  }
}

.answer-state p {
  margin-top: 0.75rem;
  color: $ink-sub;
}

.state {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  background: $soft;
  font-family: $display;
  font-weight: 700;
  color: $forest;

  &.is-assigned {
    background: $lime;
  }
  &.is-waitlist {
    background: $warn-bg;
    color: $warn-ink;
  }
}

.bottom {
  display: grid;
  gap: 0.75rem;
  margin-top: auto;
  padding-top: 1rem;
}

.btn-lime,
.btn-outline,
.btn-dark {
  @include pill-button;
  min-height: 3.5rem;
  font-size: 1.125rem;
  border: 1.5px solid transparent;
  text-align: center;
}

.btn-lime {
  border-color: $lime;
  background: $lime;
  color: $forest;
}

.btn-dark {
  border-color: $forest;
  background: $forest;
  color: #fff;
}

.btn-outline {
  border-color: $forest;
  background: #fff;
  color: $forest;

  &.danger {
    border-color: #f0c9c2;
    color: #a32c1f;
  }
}

.fine {
  font-size: 0.875rem;
  text-align: center;
  color: $ink-sub;
}

.earn-list {
  display: grid;
  gap: 0.5rem;
}

.earn-row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 0.75rem;
  align-items: baseline;

  span:first-child {
    text-transform: capitalize;
  }

  strong {
    font-weight: 600;
    color: $forest;
  }
}

.pending-text {
  margin-bottom: 1.25rem;
  color: $ink-sub;
}

.pending-btn {
  width: 100%;
}

.home-tag.is-waiting {
  background: $soft;
}

// Swap modal: own overlay, above the page
.swap-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(10, 30, 25, 0.55);
  outline: 0;
}

.swap-card {
  width: 100%;
  max-width: 28rem;
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  background: #fff;
}

.swap-title {
  font-family: $display;
  font-size: 1.25rem;
  font-weight: 600;
  color: $forest;
}

.swap-text,
.swap-empty {
  margin: 0.375rem 0 1rem;
  font-size: 0.9375rem;
  color: $ink-sub;
}

.swap-option {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.25rem 0.75rem;
  width: 100%;
  margin-bottom: 0.5rem;
  padding: 0.875rem 1rem;
  border: 1.5px solid $line;
  border-radius: 12px;
  background: #fff;
  font: inherit;
  text-align: left;
  color: $ink;
  cursor: pointer;

  &.is-on {
    border-color: $forest;
    background: $soft;
  }

  // Not eligible: shown, but can't be chosen
  &.is-off {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.swap-name {
  font-weight: 600;
  color: $forest;
}

.swap-seats {
  font-size: 0.875rem;
  color: $ink-sub;
}

.swap-reason {
  grid-column: 1 / -1;
  font-size: 0.8125rem;
  color: $warn-ink;
}

.swap-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 1rem;
}
</style>
